import { AdsCampaign, DynamicPageConnection } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { deleteField } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import facebookAdsIcon from '../../../assets/images/facebookAdsIcon.png';
import googleAdsIcon from '../../../assets/images/googleAdsIcon.png';
import ChipsAutocomplete from '../../../components/Common/ChipsAutocomplete';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import { getCachedCampaigns } from '../../../services/AdsAccountsService';
import { updateDynamicPage } from '../../../services/DynamicPagesService';

function getConnectionTypeLabel(connection: DynamicPageConnection) {
  switch (connection.type) {
    case 'adsAccount':
      return 'Ads Account: ';

    case 'adsCampaign':
      return 'Ads Campaign: ';
    default:
      return '';
  }
}

export const ManageDynamicPageSettingsAdsAccountsConnections = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, adsAccounts, analyticsRange } =
    useContext(DashboardContext);

  const [adsCampaigns, setAdsCampaigns] = useState<AdsCampaign[]>();

  const updateDynamicPageData = useCallback(
    async (data: any, mergeFields: string[]) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          selectedDynamicPage.id,
          data,
          mergeFields,
        );
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, show],
  );

  useEffect(() => {
    if (!selectedAccount) {
      setAdsCampaigns(undefined);
      return;
    }

    getCachedCampaigns(selectedAccount?.id)
      .then((res) => {
        setAdsCampaigns(res);
      })
      .catch((e) => {
        Sentry.captureException(e);
        show(e.message, 'warn');
      });
  }, [selectedAccount, analyticsRange, show]);

  return (
    <div>
      <SettingSectionTitle
        title="Connected Ads Accounts and Campaigns"
        description="Connect Ads accounts and Campaigns to get analytics and optimize by."
      />

      <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
        <div className="pt-6 sm:flex">
          <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
            Connections
          </dt>
          <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
            <ChipsAutocomplete<DynamicPageConnection>
              loading={!adsAccounts || !adsCampaigns}
              getValue={(c) => c.name}
              renderItem={(c) => (
                <span className="inline-flex items-center gap-x-2">
                  <img
                    src={
                      c.provider === 'google' ? googleAdsIcon : facebookAdsIcon
                    }
                    className="size-3"
                  />
                  {`${getConnectionTypeLabel(c)}${c.name}`}
                </span>
              )}
              selected={
                selectedDynamicPage?.data()?.connections
                  ? Object.values(selectedDynamicPage.data().connections ?? {})
                  : []
              }
              onChange={(selected) => {
                const toRemove = Object.keys(
                  selectedDynamicPage?.data()?.connections ?? {},
                ).filter(
                  (k) => !selected.some((s) => s.id === k.split('-')[1]),
                );
                const update = Object.fromEntries([
                  ...selected.map((s) => [`${s.provider}-${s.id}`, s]),
                  ...toRemove.map((k) => [`${k}`, deleteField()]),
                ]);

                updateDynamicPageData(
                  { connections: update },
                  Object.keys(update).map((k) => `connections.${k}`),
                );
              }}
              getOptionId={(t) => t.id}
              options={[
                ...(adsCampaigns?.map(
                  (c) =>
                    ({
                      id: c.id,
                      name: c.name,
                      type: 'adsCampaign',
                      provider: c.provider,
                      adsAccountId: c.adsAccountId,
                    }) satisfies DynamicPageConnection,
                ) ?? []),
                ...(adsAccounts?.map(
                  (a) =>
                    ({
                      id: a.id,
                      name: a.data().name,
                      type: 'adsAccount',
                      provider: a.data().provider,
                      adsAccountId: a.id,
                    }) satisfies DynamicPageConnection,
                ) ?? []),
              ]}
              className="w-full"
            />
          </dd>
        </div>
      </dl>
    </div>
  );
};
